import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form } from '@douyinfe/semi-ui';
import { IconEdit, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import ObjSelector from '../../components/ObjSelector';
import FieldValueInput from '../../components/FieldValueInput';
import styles from '../AllFieldVals/index.module.scss';
import { useLocation } from 'react-router-dom';

const objTypeMap = {
    0: { text: '用户', type: 'primary' },
    1: { text: '员工', type: 'success' },
};

const AllFieldValObjs = () => {
    const location = useLocation();
    // 解析url参数
    const getQueryParam = (name) => {
        const params = new URLSearchParams(location.search);
        return params.get(name);
    };
    const urlObjType = Number(getQueryParam('obj_type'));
    const urlTyp = Number(getQueryParam('typ')) || 10;

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(true);
    const [editFormApi, setEditFormApi] = useState(null);
    const [searchKey, setSearchKey] = useState('');
    // 直接用url参数初始化，不允许用户切换
    const [searchObjType] = useState(isNaN(urlObjType) ? 0 : urlObjType);
    const [fieldList, setFieldList] = useState([]);
    const [selectedObj, setSelectedObj] = useState(null);
    const [selectedObjUid, setSelectedObjUid] = useState('');
    const [singleEditVisible, setSingleEditVisible] = useState(false);
    const [singleEditLoading, setSingleEditLoading] = useState(false);
    const [singleEditValObj, setSingleEditValObj] = useState(null);
    const [singleEditRecord, setSingleEditRecord] = useState(null);

    // 获取字段列表
    useEffect(() => {
        const fetchFieldList = async () => {
            try {
                const response = await request.get('/go/sp/all/list_all_field/', {
                    params: {
                        page: 0,
                        limit: 100,
                        status: 0,
                        typ: urlTyp
                    }
                });
                if (response.errno === 0) {
                    setFieldList(response.list || []);
                } else {
                    Toast.error(response.errstr || '获取字段列表失败');
                }
            } catch (error) {
                Toast.error('获取字段列表失败');
            }
        };
        fetchFieldList();
    }, [urlTyp]);

    // 获取对象列表
    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_field_val_obj/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    obj_type: searchObjType,
                    typ: urlTyp
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, [searchObjType, urlTyp]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    // 动态生成字段列
    const fieldColumns = fieldList.map(field => {
        const rule = field.rule ? JSON.parse(field.rule) : {};
        const displayName = rule.n || field.ukey || field.name || field.uid;
        return {
            title: displayName,
            dataIndex: field.uid,
            render: (_, record) => {
                const valObj = (record.vals || []).find(v => v.field_uid === field.uid);
                return (
                    <div
                        style={{ fontSize: '12px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => {
                            setSingleEditRecord(record);
                            if (valObj) {
                                setSingleEditValObj(valObj);
                            } else {
                                setSingleEditValObj({
                                    uid: '',
                                    field_uid: field.uid,
                                    val: ''
                                });
                            }
                            setSingleEditVisible(true);
                        }}
                        title="点击编辑"
                    >
                        {valObj?.val || '-'}
                    </div>
                );
            }
        };
    });

    const columns = [
        {
            title: '对象',
            dataIndex: 'obj',
            width: 200,
            render: (obj, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>
                        {obj?.name || record.obj_uid || '-'}
                    </div>
                </div>
            )
        },
        ...fieldColumns,
        {
            title: '创建/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 120,
            render: (_, record) => (
                <Button
                    type='primary'
                    icon={<IconEdit />}
                    onClick={() => {
                        setSelectedObj(record);
                        setSelectedObjUid(record.obj_uid);
                        setIsEditMode(true);
                        setShowEditModal(true);
                    }}
                >
                    编辑
                </Button>
            )
        }
    ];

    // 编辑/新建弹窗表单
    const renderEditForm = () => {
        const currentVals = (selectedObj?.vals || []);
        return (
            <Form
                getFormApi={setEditFormApi}
                initValues={{
                    obj_uid: isEditMode ? selectedObjUid : '',
                    fieldValues: fieldList.map(field => {
                        const valObj = currentVals.find(v => v.field_uid === field.uid);
                        return { field_uid: field.uid, val: valObj ? valObj.val : '' };
                    })
                }}
                onSubmit={values => {
                    const filteredValues = (values.fieldValues || [])
                        .filter(item => item.val !== '' && item.val !== undefined)
                        .map(item => ({
                            ...item,
                            val: Array.isArray(item.val) ? item.val.join(',') : item.val
                        }));
                    handleEdit({ ...values, fieldValues: filteredValues });
                }}
                style={{ padding: '20px' }}
            >
                <Form.Slot label={`对象`}>
                    {isEditMode ? (
                        <ObjSelector
                            obj_type={searchObjType}
                            value={selectedObjUid}
                            disabled
                            placeholder={`请选择${objTypeMap[searchObjType]?.text || '对象'}`}
                            single={true}
                        />
                    ) : (
                        <ObjSelector
                            obj_type={searchObjType}
                            value={selectedObjUid}
                            onChange={value => {
                                const selectedValue = Array.isArray(value) ? value[0] : value;
                                editFormApi?.setValue('obj_uid', selectedValue);
                                setSelectedObjUid(selectedValue);
                            }}
                            placeholder={`请选择${objTypeMap[searchObjType]?.text || '对象'}`}
                            single={true}
                        />
                    )}
                </Form.Slot>
                <Form.Slot label="字段值列表">
                    <div style={{ border: '1px solid var(--semi-color-border)', borderRadius: '6px', padding: '16px' }}>
                        {fieldList.map((field, index) => {
                            const rule = field.rule ? JSON.parse(field.rule) : {};
                            return (
                                <div key={field.uid} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-end' }}>
                                    <FieldValueInput
                                        rule={rule}
                                        field={`fieldValues.${index}.val`}
                                        value={editFormApi?.getValue(`fieldValues.${index}.val`)}
                                        onChange={v => editFormApi?.setValue(`fieldValues.${index}.val`, v)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    // 编辑/新建接口
    const handleEdit = async (values) => {
        if (!values.obj_uid) {
            Toast.error('请选择对象');
            return;
        }
        try {
            const vals = values.fieldValues.map(item => ({
                field_uid: item.field_uid,
                val: item.val
            }));
            const response = await request.post('/go/sp/all/set_all_field_val/', {
                obj_uid: values.obj_uid,
                vals: JSON.stringify(vals)
            });
            if (response.errno === 0) {
                Toast.success(`${isEditMode ? '编辑' : '新建'}成功，共设置 ${response.cnt} 个字段值`);
                setShowEditModal(false);
                fetchList(pagination.currentPage, pagination.pageSize);
            } else {
                Toast.error(response.errstr || `${isEditMode ? '编辑' : '新建'}失败`);
            }
        } catch (error) {
            Toast.error(`${isEditMode ? '编辑' : '新建'}失败`);
        }
    };

    return (
        <div className={styles.allFieldValsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索对象"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button
                        type="primary"
                        icon={<IconPlus />}
                        onClick={() => {
                            setIsEditMode(false);
                            setSelectedObj(null);
                            setSelectedObjUid('');
                            setShowEditModal(true);
                        }}
                    >
                        新建
                    </Button>
                </Space>
            </div>
            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                scroll={{ x: 200 + fieldList.length * 180 + 300 }}
            />
            <Modal
                title={isEditMode ? "编辑字段值" : "新建字段值"}
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderEditForm()}
            </Modal>
            <Modal
                title={singleEditValObj ? (() => {
                    const field = fieldList.find(f => f.uid === singleEditValObj.field_uid);
                    if (!field) return '编辑字段';
                    const rule = field.rule ? JSON.parse(field.rule) : {};
                    return `编辑字段：${rule.n || field.ukey || field.name || field.uid}`;
                })() : ''}
                visible={singleEditVisible}
                onCancel={() => setSingleEditVisible(false)}
                footer={null}
                centered
                width={400}
            >
                {singleEditValObj && singleEditRecord && (() => {
                    const field = fieldList.find(f => f.uid === singleEditValObj.field_uid);
                    let rule = {};
                    if (field) {
                        try {
                            rule = field.rule ? JSON.parse(field.rule) : {};
                        } catch (e) {
                            console.error('rule 解析失败', field.rule, e);
                            rule = {};
                        }
                    }
                    if (!field) return null;
                    return (
                        <Form
                            initValues={{ val: singleEditValObj.val }}
                            onValueChange={vals => setSingleEditValObj({ ...singleEditValObj, val: vals.val })}
                            onSubmit={async () => {
                                setSingleEditLoading(true);
                                try {
                                    const resp = await request.post('/go/sp/all/set_all_field_val/', {
                                        obj_uid: singleEditRecord.obj_uid,
                                        vals: JSON.stringify([
                                            {
                                                field_uid: singleEditValObj.field_uid,
                                                val: Array.isArray(singleEditValObj.val) ? singleEditValObj.val.join(',') : singleEditValObj.val
                                            }
                                        ])
                                    });
                                    if (resp.errno === 0) {
                                        Toast.success('编辑成功');
                                        setSingleEditVisible(false);
                                        fetchList(pagination.currentPage, pagination.pageSize);
                                    } else {
                                        Toast.error(resp.errstr || '编辑失败');
                                    }
                                } catch (e) {
                                    Toast.error('编辑失败');
                                } finally {
                                    setSingleEditLoading(false);
                                }
                            }}
                        >
                            <FieldValueInput
                                rule={rule}
                                field="val"
                                value={singleEditValObj.val}
                                onChange={v => {
                                    setSingleEditValObj({ ...singleEditValObj, val: v });
                                }}
                            />
                            <div style={{ marginTop: 24, textAlign: 'right' }}>
                                <Space>
                                    <Button onClick={() => setSingleEditVisible(false)}>取消</Button>
                                    <Button type="primary" htmlType="submit" loading={singleEditLoading}>确定</Button>
                                </Space>
                            </div>
                        </Form>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default AllFieldValObjs; 